import { config, IJsonRpcResponse } from "../api";
import { ICurrency } from '../common';

interface IReport extends IJsonRpcResponse {
    result: IFinanceReport,
    error: IFinanceReportError|undefined,
}

export interface IFinanceReport {
    title: string,
    headers: string[],
    rows: Array<Array<string | number>>,
}

export interface IFinanceReportError {
    code: number,
    message: string;
}

export interface UnsuccessfulChargeHistoryReponse {
  error: {
    message: string;
  };
  success: false;
}

export interface SuccessfulChargeHistoryResponse {
    creditAmount: string,
    creditBalance: string,
    currency: ICurrency,
    customer: ICustomer,
    invoices: IInvoice[]
    subscriptionDetails: ISubscription,
    success: true,
    processorName: string,
}

interface IGetChargeHistoryResponse extends IJsonRpcResponse {
  result: SuccessfulChargeHistoryResponse | UnsuccessfulChargeHistoryReponse;
}

interface ICredit {
    currentAvailableCredits: number,
    totalCredits: number,
}

export interface ICustomer {
    companyProfileID: number,
    customerName: string,
    gracePeriod: string | undefined,
    id: number,
    isReseller: boolean,
    isMailPlus: boolean,
    isMarketingAutomation: boolean,
    isActive: boolean,
}

export interface IInvoice {
    amountBilled: string,
    amountFromCard: string,
    amountFromCredit: string,
    currency: ICurrency,
    id: number,
    lineItems: ILineItem[],
    note: string | null,
    settlementDate: string,
    statementDate: string
    transactionID: string,
    usageDetails: IUsageDetail[],
    isPendingAuth: boolean
}

// Future improvement: split refund items out of invoice.lineItems
export const getNonRefundItems = (invoice: IInvoice) =>
  invoice.lineItems !== null ? invoice.lineItems.filter(item => item.type !== 'refund') : [];
export const getRefundItems = (invoice: IInvoice) =>
  invoice.lineItems !== null ? invoice.lineItems.filter(item => item.type === 'refund') : [];

export const lineItemRefundSum =  (item:ILineItem):number=> {
    return item.refunds.reduce((pre, refund) => pre + refund.amount, 0);
};

export interface ILineItem {
    amount: number;
    amountFromCredit: number;
    formattedAmount: string,
    childCompanyName?: string,
    childCompanyID: number | null
    description: string,
    id: number,
    units: number,
    usageDetailID: number
    type?: string,
    product?: IProduct,
    refunds: LineItemRefund[]
}

interface IProduct {
    id: number,
    name: string,
    translationKey: string,
    price: number,
    currencyID: number,
    sku: string,
    productType: string,
    isPlan: boolean,
    createTimestamp: string,
    updateTimestamp: string
}

interface LineItemRefund {
    id : number,
    invoiceItemID : number,
    refundID : number,
    amount : number,
    createTimestamp : string,
}

interface IResetNotificationRequestResponse extends IJsonRpcResponse {
    result: {
        success: boolean,
        error: {
            message: string,
        },
    },
}

interface IToolshedFormatRequestResponse extends IJsonRpcResponse {
    result: {
        success: boolean,
        errors: {
            message: string,
        },
        data: any,
    },
}

interface IPaymentRetryResultResponse extends IJsonRpcResponse {
    result: {
        status: string,
        message: string,
    },
}

interface ISetGracePeriodRequestResponse extends IJsonRpcResponse {
    result: {
        success: boolean,
        error: {
            message: string,
        },
        customer: any,
    }
}

export interface ISubscription {
    activeClients: number | null,
    amountToBeBilled: number | null,
    emailsUsed?: number | null,
    initialPayment?: number | null,
    leads?: number | null,
    maxClients?: number | null,
    maxEmails?: number | null,
    nextChargeDate: string,
    planPrice: number,
    planType: string,
    usageDetails: IUsageDetail[],
}

export interface IUsageDetail {
    id: number,
    companyName: string,
    companyProfileID: number,
    customerID: number,
    leads?: number | null,
    emails?: number | null,
    pageImpressions?: number | null,
    hasExternalCRM?: boolean | null,
    litmusTests?: number | null,
    shutterstockImages?: number | null,
    pieSyncConnections?: number | null,
    mailboxes?: number | null,
    proClients?: number | null,
    espClients?: number | null,
    inactiveClients?: number | null,
    activeLicenses?: number | null,
}


export const getChargeHistory = async (companyID: number, authToken: string) => {
    const res = await fetch(`${config.url}/reporting/chargeHistory/${companyID}`, {
        headers: {
            'Authorization': 'Bearer ' + authToken,
            'Content-Type': 'application/json'
        },
        method: 'get',
        mode: 'cors'
    });

    const body: IGetChargeHistoryResponse = await res.json();
    return body.result;
};

export const getFinanceReport = async (reportType: 'billing' | 'payments',
    startDate: string,
    endDate: string,
    companyProfileID: number|undefined
) => {
    const authToken = localStorage.getItem('token');
    const data = {
        companyProfileID,
        endDate,
        startDate
    };

    const res = await fetch(`${config.url}/reporting/${reportType}`, {
        body: JSON.stringify(data),
        headers: {
            'Authorization': 'Bearer ' + authToken,
            'Content-Type': 'application/json'
        },
        method: 'post',
        mode: 'cors'
    });

    const body: IReport = await res.json();
    if (res.status === 200) {
        return body.result;
    }

    throw body.error;
};

export const resetBillingNotifications = async (companyID: number) => {
    const data = {
        companyID
    };

    const res = await fetch(`${config.url}/resetBillingNotifications`, {
        body: JSON.stringify(data),
        headers: {
            'Authorization': 'Bearer ' + config.authToken,
            'Content-Type': 'application/json'
        },
        method: 'post',
        mode: 'cors'
    });

    const body: IResetNotificationRequestResponse = await res.json();
    return body.result;
};

export const setGracePeriod = async (customerID: number, gracePeriod: string) => {
    const data = {
        customerID,
        gracePeriod,
    };

    const res = await fetch(`${config.url}/setGracePeriod`, {
        body: JSON.stringify(data),
        headers: {
            'Authorization': 'Bearer ' + config.authToken,
            'Content-Type': 'application/json',
        },
        method: 'post',
        mode: 'cors',
    });

    const body: ISetGracePeriodRequestResponse = await res.json();
    return body.result;
}

export const retryTransaction = async (invoiceID: number) => {
    const data = {
        invoiceID
    };

    const res = await fetch(`${config.url}/retryTransaction`, {
        body: JSON.stringify(data),
        headers: {
            'Authorization': 'Bearer ' + config.authToken,
            'Content-Type': 'application/json'
        },
        method: 'post',
        mode: 'cors'
    });

    const body: IPaymentRetryResultResponse = await res.json();
    return body.result;
}

export const voidTransaction = async (invoiceID: number) => {
    const data = {
        invoiceID
    };

    const res = await fetch(`${config.url}/voidTransaction`, {
        body: JSON.stringify(data),
        headers: {
            'Authorization': 'Bearer ' + config.authToken,
            'Content-Type': 'application/json'
        },
        method: 'post',
        mode: 'cors'
    });

    const body: IToolshedFormatRequestResponse = await res.json();
    return body.result;
}
